import {
    arrayRemove,
    arrayUnion,
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    onSnapshot,
    query,
    setDoc,
    Timestamp,
    updateDoc,
    where,
} from 'firebase/firestore'
import { db, storage } from '../../firebase-info'
import { STATUS_NOTIFIED } from './constants'
import { deleteObject, getBlob, ref, uploadBytes } from 'firebase/storage'

const createElementDoc = (coll, element) => {
    const elemRef = doc(collection(db, coll))

    const finalElement = {
        ...element,
        id: elemRef.id,
    }

    setDoc(elemRef, finalElement)
    return elemRef
}

const insertElementInCommunity = (commRef, arrayName, elementRef) => {
    const data = {}
    data[arrayName] = arrayUnion(elementRef)
    updateDoc(commRef, data)
}

const updateUnseenArrays = (neighboursRefs, elementRef, excluded = []) => {
    neighboursRefs.forEach((neighRef) => {
        if (!excluded.find((n) => neighRef.id === n.id)) {
            updateDoc(neighRef, { unseen: arrayUnion(elementRef) })
        }
    })
}

const updateUnseenArraysWithMatchingRoles = (
    neighboursData,
    elementRef,
    roles,
    excluded = []
) => {
    neighboursData.forEach((neighData) => {
        if (!excluded.find((n) => n.id === neighData.id)) {
            if (roles.some((r) => neighData.roles.includes(r))) {
                updateDoc(neighData.ref, { unseen: arrayUnion(elementRef) })
            }
        }
    })
}

const subscribeToDocument = (collection, id, callback) => {
    return onSnapshot(doc(db, collection, id), (doc) => {
        callback(doc)
    })
}

const getDataOf = async (ref) => {
    if (!ref) {
        return {}
    }
    const snap = await getDoc(ref)
    if (snap.exists()) {
        return { ...snap.data(), ref: ref }
    }

    return {}
}

const getDataOfRefs = async (refArray) => {
    if (!refArray?.length) {
        return []
    }

    const promises = refArray.map(async (elem) => {
        return await getDataOf(elem)
    })

    return await Promise.all(promises)
}

const markAsSeen = (neighbourRef, elemRef) => {
    try {
        if (elemRef) {
            updateDoc(neighbourRef, { unseen: arrayRemove(elemRef) })
        }
    } catch (e) {
        console.log(e)
    }
}

const changeCurrentCommunity = async (userRef, selectedNbRef) => {
    if (userRef && selectedNbRef) {
        // Update user doc with the new neighbour reference and set as active
        const user = await getDataOf(userRef)

        user['activeNeighbour'] = selectedNbRef

        delete user.ref

        updateDoc(userRef, user)
    }
}

const getDestinatariesOf = (matchingRoles, neighRef, neighboursData) => {
    const ret = [neighRef]
    neighboursData.forEach((n) => {
        if (n.roles.some((r) => matchingRoles.includes(r))) {
            if (!ret.find((el) => el.id === n.id)) {
                ret.push(n.ref)
            }
        }
    })

    return ret
}

const createTicket = (ticket, commRef, neighRef, neighboursData, messages) => {
    delete ticket.description
    const destinatariesRefs = getDestinatariesOf(
        ticket.to,
        neighRef,
        neighboursData
    )

    const finalTicket = {
        ...ticket,
        creationDate: Timestamp.fromDate(new Date()),
        publishingDate: Timestamp.fromDate(new Date()),
        published: true,
        communityRef: commRef,
        status: STATUS_NOTIFIED,
        chats: messages,
        owner: neighRef,
        to: destinatariesRefs,
    }

    const ticketRef = createElementDoc('tickets', finalTicket)

    insertElementInCommunity(commRef, 'tickets', ticketRef)

    updateUnseenArraysWithMatchingRoles(neighboursData, ticketRef, ticket.to)

    return ticketRef
}

const createDelivery = (
    delivery,
    commRef,
    destinataryNeighRef,
    destinataryPropertyRef,
    neighRef
) => {
    const finalDelivery = {
        ...delivery,
        creationDate: Timestamp.fromDate(new Date()),
        publishingDate: Timestamp.fromDate(new Date()),
        published: true,
        communityRef: commRef,
        owner: neighRef,
        toNeighbourRef: destinataryNeighRef || null,
        toPropertyRef: destinataryPropertyRef,
    }

    const deliveryRef = createElementDoc('deliveries', finalDelivery)

    insertElementInCommunity(commRef, 'deliveries', deliveryRef)

    if (destinataryNeighRef) {
        updateUnseenArrays([destinataryNeighRef], deliveryRef)
    }

    return deliveryRef
}

const downloadAttachment = async ({ name, url }) => {
    // file object
    const fileRef = ref(storage, url)
    const file = await getBlob(fileRef)
    // anchor link
    const element = document.createElement('a')
    element.href = URL.createObjectURL(file)
    element.download = name
    // simulate link click
    document.body.appendChild(element) // Required for this to work in FireFox
    element.click()
}

const openAttachmentInNewTab = async ({ url }) => {
    // file object
    const fileRef = ref(storage, url)
    const file = await getBlob(fileRef)

    window.open(URL.createObjectURL(file), '_blank', 'noopener,noreferrer')
}

const getAttachmentBlobfromStorageURL = async ({ url }) => {
    // file object
    const fileRef = ref(storage, url)
    return await getBlob(fileRef)
}

const getAttachmentURLfromStorageURL = async ({ url }) => {
    // file object
    const fileRef = ref(storage, url)
    const file = await getBlob(fileRef)

    return URL.createObjectURL(file)
}

const loadCommunitiesOfUser = async (userData) => {
    const neighbourListOfUser = userData.neighbours
    if (neighbourListOfUser) {
        const promises = neighbourListOfUser.map(async (nb) => {
            const nbData = await getDataOf(nb)
            if (nbData) {
                const commData = await getDataOf(nbData.communityRef)

                return {
                    nbRef: nb,
                    ...commData,
                    hasNews: nbData.unseen.length > 0,
                }
            }
        })

        return await Promise.all(promises)
    }
}

const uploadFilesRelatedTo = async (folder, files, elemRef) => {
    const newFiles = []
    await Promise.all(
        files.map(async (file) => {
            if (file instanceof File) {
                const fileRef = ref(
                    storage,
                    `attachments/${folder}/${elemRef.id}/${file.name}`
                )
                await uploadBytes(fileRef, file)
                newFiles.push({ name: file.name, url: fileRef.fullPath })
            }
        })
    )
    updateDoc(elemRef, { files: arrayUnion(...newFiles) })
}

const createFilesFromDocument = async (files) => {
    if (files) {
        const ret = files.map(async (attachment) => {
            const fileRef = ref(storage, attachment.url)
            const blob = await getBlob(fileRef)
            return new File([blob], attachment.name, {
                type: blob.type,
            })
        })

        return await Promise.all(ret)
    }
    return []
}

const loadOnlyImages = async (files) => {
    const attImgs = await Promise.all(
        files.map(async (f) => {
            const blob = await getAttachmentBlobfromStorageURL(f)
            const allowedMimeTypes = ['image/jpeg', 'image/png']
            if (allowedMimeTypes.find((el) => el === blob.type)) {
                return { name: f.name, url: URL.createObjectURL(blob) }
            }
        })
    )

    return attImgs.filter((el) => (el ? true : false))
}

const deleteAnnounce = async (announce) => {
    // Delete attachments from storage
    announce.files.forEach((file) => {
        const fileRef = ref(storage, file.url)
        deleteObject(fileRef)
    })
    // Delete announce ref from array in community
    updateDoc(announce.communityRef, { announces: arrayRemove(announce.ref) })

    // Delete announce ref from all unseen arrays in neighbours in community
    const communityData = await getDataOf(announce.communityRef)
    communityData.neighbours.forEach((nRef) => {
        markAsSeen(nRef, announce.ref)
    })

    // Delete announce document
    deleteDoc(announce.ref)
}

const checkNeighbourCodeAvailable = async (code) => {
    const neighbourSnap = await getDoc(doc(db, 'neighbours', code))
    if (neighbourSnap.exists()) {
        return neighbourSnap
    }
    return null
}

const checkAdminCodeAvailable = async (code) => {
    const codeSnap = await getDoc(doc(db, 'admincodes', code))
    if (codeSnap.exists()) {
        return codeSnap
    }

    return null
}

const joinUserToCommunity = async (userRef, neighbourRef) => {
    updateDoc(userRef, {
        neighbours: arrayUnion(neighbourRef),
        activeNeighbour: neighbourRef,
    })
    updateDoc(neighbourRef, { userRef: userRef })
}

const deleteReservationsOfFacility = async (facilityRef) => {
    const qry = query(
        collection(db, 'reservations'),
        where('facilityRef', '==', facilityRef)
    )

    const querySnapshot = await getDocs(qry)
    querySnapshot.forEach((doc) => {
        deleteDoc(doc.ref)
    })
}

const getReservationsWithDate = async (
    facilityRef,
    date,
    withNeighbourData = false
) => {
    const start = Timestamp.fromDate(new Date(date.getTime() - 60 * 1000)) // -1 min
    const end = Timestamp.fromDate(new Date(date.getTime() + 60 * 1000)) // +1 min

    const qry = query(
        collection(db, 'reservations'),
        where('facilityRef', '==', facilityRef),
        where('reservationDate', '>=', start),
        where('reservationDate', '<=', end)
    )

    const querySnapshot = await getDocs(qry)
    if (withNeighbourData) {
        const ret = await Promise.all(
            querySnapshot.docs.map(async (r) => {
                const neigh = await getDataOf(r.data().neighbourRef)
                return {
                    ...r.data(),
                    ref: r.ref,
                    id: r.id,
                    neighbourData: neigh,
                }
            })
        )
        return ret
    }
    return querySnapshot.docs
}

const updateLastConnectionInTicket = async (ticketRef, neighbourRef, when) => {
    const ticketSnap = await getDoc(ticketRef)
    const ticketData = ticketSnap.data()
    let conns

    if (ticketData.lastConnections) {
        conns = [...ticketData.lastConnections]
        const el = conns.find((el) => el.neighbourRef.id === neighbourRef.id)
        if (el) {
            el.when = Timestamp.fromDate(when.toDate())
        } else {
            conns.push({
                neighbourRef,
                when: Timestamp.fromDate(when.toDate()),
            })
        }
    } else {
        conns = []
        conns.push({
            neighbourRef,
            when: Timestamp.fromDate(when.toDate()),
        })
    }
    updateDoc(ticketRef, { lastConnections: conns })
}

export {
    createElementDoc,
    insertElementInCommunity,
    updateUnseenArrays,
    updateUnseenArraysWithMatchingRoles,
    subscribeToDocument,
    getDataOf,
    getDataOfRefs,
    markAsSeen,
    changeCurrentCommunity,
    createTicket,
    createDelivery,
    downloadAttachment,
    openAttachmentInNewTab,
    getAttachmentBlobfromStorageURL,
    getAttachmentURLfromStorageURL,
    loadCommunitiesOfUser,
    uploadFilesRelatedTo,
    createFilesFromDocument,
    deleteAnnounce,
    checkNeighbourCodeAvailable,
    checkAdminCodeAvailable,
    joinUserToCommunity,
    deleteReservationsOfFacility,
    getReservationsWithDate,
    updateLastConnectionInTicket,
    loadOnlyImages,
}
